import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { X } from 'lucide-react';

/* ═══════════════════════════════════════════════════════════════════════════
   FENÊTRE AU PREMIER PLAN

   La présentation des candidatures coach et mentor, pour tout formulaire
   qui s'ouvre au-dessus de la page en cours : signaler un problème,
   programmer une séance, rédiger un compte rendu ou un rapport. Le
   formulaire ne pousse plus la page vers le bas, et on ne perd pas sa
   place en le fermant.

   Échap, un clic à côté ou la croix ferment. `nu` : le contenu porte déjà
   sa carte et sa croix (la fiche de séance), la fenêtre n'ajoute que le
   fond et le cadre.
   ═══════════════════════════════════════════════════════════════════════════ */

const LARGEURS = { normale: 'max-w-xl', large: 'max-w-3xl' };

export default function Fenetre({ titre, icone: Icone, onClose, largeur = 'normale', nu = false, children }) {
  const boite = useRef(null);
  const idTitre = useRef(`fenetre-${Math.random().toString(36).slice(2, 8)}`).current;

  // La page dessous ne défile plus, et le clavier arrive dans la fenêtre.
  useEffect(() => {
    const memoire = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    boite.current?.focus();
    return () => { document.body.style.overflow = memoire; };
  }, []);

  useEffect(() => {
    const auClavier = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', auClavier);
    return () => document.removeEventListener('keydown', auClavier);
  }, [onClose]);

  return createPortal(
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        transition={{ duration: .2 }}
        className="fixed inset-0 z-[100] overflow-y-auto p-0 sm:p-6 flex sm:items-center"
        style={{ background: 'rgba(10,17,40,.6)', backdropFilter: 'blur(3px)' }}
        onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
        role="presentation"
      >
        <motion.div
          initial={{ opacity: 0, y: 16, scale: .985 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 10 }}
          transition={{ duration: .28, ease: [.22, 1, .36, 1] }}
          ref={boite} tabIndex={-1} role="dialog" aria-modal="true"
          aria-labelledby={titre ? idTitre : undefined} aria-label={titre ? undefined : 'Fenêtre'}
          className={`relative w-full ${LARGEURS[largeur] || LARGEURS.normale} mx-auto outline-none sm:my-auto
                      ${nu ? '' : 'bg-[var(--bg-card)] min-h-screen sm:min-h-0 sm:rounded-cadre shadow-carte'}`}
          style={nu ? undefined : { boxShadow: '0 25px 50px -12px rgba(0,0,0,.25)' }}
        >
          {nu ? children : (
            <>
              <button type="button" onClick={onClose} aria-label="Fermer"
                      className="act act-icone act-tertiaire absolute top-3.5 right-3.5">
                <X className="w-[18px] h-[18px]" aria-hidden="true" />
              </button>
              <div className="p-6 sm:p-8">
                {titre && (
                  <h2 id={idTitre}
                      className="flex items-center gap-2.5 text-xl font-semibold text-[var(--text-bold)] mb-4 pr-10">
                    {Icone && <Icone className="w-5 h-5 text-[var(--accent-texte)]" aria-hidden="true" />}
                    {titre}
                  </h2>
                )}
                {children}
              </div>
            </>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>,
    document.body,
  );
}
